import { useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface UseAuthRedirectOptions {
  redirectTo?: string;
  redirectWhenAuthenticated?: boolean;
}

/**
 * Post-auth destination resolution, in order of precedence:
 *   1. `?return=<path>` query param (used by `SessionExpiredBanner`
 *      to send the user back where they were after a mid-session
 *      token-expiry re-login).
 *   2. `location.state.from.pathname` (router-state form used by some
 *      ProtectedRoute guards when they redirect to /auth).
 *   3. `redirectTo` option (defaults to `/`).
 *
 * The `?return=` value is decoded and sanity-checked - only same-origin
 * paths starting with `/` are honored, to avoid open-redirect issues.
 */
export function useAuthRedirect(options: UseAuthRedirectOptions = {}) {
  const { redirectTo = '/', redirectWhenAuthenticated = true } = options;

  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (loading) return;

    if (redirectWhenAuthenticated && user) {
      const rawReturn = searchParams.get('return');
      const decoded = rawReturn ? decodeURIComponent(rawReturn) : null;
      // Only honor relative paths to dodge open-redirect attacks.
      const safeReturn =
        decoded && decoded.startsWith('/') && !decoded.startsWith('//')
          ? decoded
          : null;

      const from = safeReturn || location.state?.from?.pathname || redirectTo;
      navigate(from, { replace: true });
    }
  }, [
    user,
    loading,
    navigate,
    redirectTo,
    location.state,
    searchParams,
    redirectWhenAuthenticated,
  ]);

  return { user, loading };
}
